import { FC, useEffect, useState } from 'react';
import './StudentProfileForm.scss';
import { useForm, SubmitHandler, FormProvider, Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMutation } from "@apollo/client";
import { getDecryptedDataWithSecretKey, getScreenDimensions, handleInputWithoutSpaces, handlePhoneNumberInput } from '../../utils/helper';
import FileUpload from '../Popups/FileUpload';
import InputField from '../InputField/InputField';
import ConfirmationPopup from '../ConfirmationPopup/ConfirmationPopup';
import ButtonStyle from '../ButtonStyle/ButtonStyle';
import ToasterWithMessage from '../toaster';
import { messages, patterns, SCHOOL_ADMIN, STUDENT_ROLE } from '../../utils/constants';
import images from '../../assets/images';
import { DELETE_STUDENT, UPDATE_STUDENT_PROFILE } from '../../pages/AdminStudents/api/AdminStudentsQuery';
import { useAppSelector } from '../../redux/app/hooks';
import { logout, setRefetchCurrentUser, setSplashvisible } from '../../redux/slices/authSlice';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

interface propsType {
    id: string;
    fullName: string;
    email: string;
    phonenumber: string;
    username: string;
    password: string;
    avatarUrl: string;
    isEdit: boolean;
    displayForm?: boolean;
    setDisplayForm?: (val: boolean) => void;
    isSubmit?: boolean;
    setIsEdit?: (val: boolean) => void;
}

interface IFormInput {
    fullName: string;
    email?: string;
    phonenumber?: string;
    username: string;
    password: string;
}

const schema = yup.object().shape({
    fullName: yup
        .string()
        .required(messages?.name)
        // .matches(patterns?.alphabetSpace, messages?.validName)
        .max(100, 'Name should not exceed 100 characters'),
    email: yup
        .string()
        .nullable()
        .notRequired() 
        .trim()
        .test('is-valid-email', messages.validEmail, function (value) {
            const { path, createError } = this;
            if (value && value.length > 0) {
                return patterns.email.test(value) || createError({ path, message: messages.validEmail });
            }
            return true;
        }),
    phonenumber:   yup
            .string()
            .optional()
         .test('is-valid-phoneNumber', "Invalid phone number", function (value) {
          const { path, createError } = this;
    
        // If the field is empty, it's valid (since it's optional)
        if (!value) return true;
    
        // Max length check
        if (value.length > 20) {
          return createError({ path, message: messages.phoneNumberLength });
        }
    
        // Regex pattern check
        if (!patterns.phoneNumber.test(value)) {
          return createError({ path, message: messages.validPhoneNumber });
        }
    
        return true;
      }),
    username: yup
        .string()
        .strict()
        .trim(messages.invalidUsername)
        .required(messages.username)
        .matches(patterns.username, messages.validUsername)
        .matches(patterns.usernameStartWithAlphabets, messages.usernameStartWithAlphabets)
        .matches(patterns.consecutiveDotsDashesUnderscores, messages.noConsecutiveDotsDashesUnderscores)
        .min(6, messages.usernameMinLength)
        .max(50, messages.usernameMaxLength)
        .test('not-only-numbers', messages.invalidUsername, value => {
            if (value) {
                return !patterns.onlyNumbers.test(value);
            }
            return true;
        }),
    password: yup
        .string()
        .strict()
        .trim(messages.invalidPassword)
        .required(messages.password)
        .matches(patterns?.password, messages?.validPassword)
        .min(8, messages.passwordMinLength)
        .max(30, messages.passwordMaxLength)
        // .matches(patterns?.upperCasePassword, messages?.passwordOneUpperCase)Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character
        // .matches(patterns?.lowerCasePassword, messages?.passwordOneLowerCase)
        // .matches(patterns?.oneNumbers, messages?.passwordOneNumber)
        // .matches(patterns?.oneSpecialCharacter, messages?.passwordOneSpecialCharater),
});

const StudentProfileForm: FC<propsType> = ({ id, fullName, email, phonenumber, username, password, avatarUrl, isEdit, displayForm, setDisplayForm, isSubmit, setIsEdit }) => {
    const { success, error } = ToasterWithMessage();
    const { solidinterfaceplus, User, teacherDeleteIcon, DeleteGif, uploadIconMobile } = images;
    const [profileImageURL, setProfileImageURL] = useState<string>();
    const [ImageURL, setImageURL] = useState<string>();
    const [isUploadOpen, setIsUploadOpen] = useState(false);
    const [openConfirmationPopup, setOpenConfirmationPopup] = useState<boolean>(false);
    const baseUrl = process.env.REACT_APP_FRONTEND_URL;
    const methods = useForm<IFormInput>({
        resolver: yupResolver(schema) as Resolver<IFormInput>,
    });
    const { handleSubmit, setFocus, reset, formState: { errors } } = methods;
    const role = useAppSelector((state) => state.auth.role);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { isMobileScreen } = getScreenDimensions();
    
    const [updateStudentProfile, { loading: updateStudentLoading }] = useMutation(UPDATE_STUDENT_PROFILE, {
        fetchPolicy: "no-cache",
        onCompleted: (data) => {
            success(`${role === STUDENT_ROLE ? "Profile" : "Student"} updated successfully`)
            if (setDisplayForm) {
                setDisplayForm(!displayForm);
            }
            if (setIsEdit) {
                setIsEdit(!false);
            }
            if (role === STUDENT_ROLE) {
                dispatch(setRefetchCurrentUser({ refetchCurrentUser: true }));
                navigate('/student-settings');
            }
            reset();
        },
        onError: (err) => {
            error(err?.message);
        }
    });

    const [deleteStudent, { loading: deleteStudentLoading }] = useMutation(DELETE_STUDENT, {
        fetchPolicy: "no-cache",
        onCompleted: (data) => {
            success("Student deleted successfully")
            if (setDisplayForm) {
                setDisplayForm(!displayForm);
            }
            reset();
        },
        onError: (err) => {
            error(err?.message);
        }
    });

    function areValuesDifferent(data: IFormInput) {
        const initialValues = {
            fullName: fullName,
            phonenumber: phonenumber,
            email: email,
            username: username,
            password: getDecryptedDataWithSecretKey(password).originalText
        };
        if (data.fullName !== initialValues.fullName) return true;
        if (data.username !== initialValues.username) return true;
        if (data.email !== initialValues.email) return true;
        if (data.phonenumber !== initialValues.phonenumber) return true;
        if (data.password !== initialValues.password) return true;

        return false;
    }

    const onSubmit: SubmitHandler<IFormInput> = async data => {       
        if (((areValuesDifferent(data)) || ((profileImageURL !== avatarUrl) && (profileImageURL !== User)))) {
            await updateStudentProfile({
                variables:
                {
                    input: {
                        sId: id,
                        fullName: data.fullName.trim(),
                        phoneNumber: data.phonenumber,
                        emailId: data.email,
                        avatarUrl:profileImageURL !== User ? profileImageURL : "",
                        ...(data?.username && (data?.username !== username) && { userName: data.username.trim() }),
                        ...(data?.password && (data?.password !== getDecryptedDataWithSecretKey(password).originalText) && { password: data.password.trim() })
                    }
                }
            }).then((result) => {
                if ((data?.username !== username || data?.password !== getDecryptedDataWithSecretKey(password).originalText) && role === STUDENT_ROLE && !result?.errors) {
                    setTimeout(() => {
                    dispatch(logout());
                    dispatch(setSplashvisible());
                    },500);
                }
            });
        } else if ((!areValuesDifferent(data))){
            error("There is no changes to update");
        }
        else {
            if (setDisplayForm) {
                setDisplayForm(!displayForm);
            }
            if (setIsEdit) {
                setIsEdit(false);
            }
            if (role === STUDENT_ROLE) {
                navigate('/student-settings');
            }
            reset();
        }
    };

    const handleDelete = () => {
        if (deleteStudentLoading === false) {
            setOpenConfirmationPopup(true);
        }
    };

    // click yes button in confirmation popup
    const onYesButtonClick = () => {
        setOpenConfirmationPopup(false);
        deleteStudent({
            variables: {
                sId: id
            }
        })
    }

    // click no button in confirmation popup
    const onNoButtonClick = () => {
        setOpenConfirmationPopup(false);
    }

    useEffect(() => {
        if (isEdit) {
            setFocus('fullName');
        }
    }, [setFocus]);

    useEffect(() => {
        reset({
            fullName: fullName,
            username: username,
            email: email,
            phonenumber: phonenumber,
            password: getDecryptedDataWithSecretKey(password).originalText
        });
        setProfileImageURL(avatarUrl ? avatarUrl : User);
        setImageURL(avatarUrl ? baseUrl+ avatarUrl : User);

    }, [id]);

    useEffect(() => {
        if (isSubmit && role === STUDENT_ROLE) {
            handleSubmit(onSubmit)();
        }
    }, [isSubmit])

    const handleClickUpload = () => {
        if (isEdit) {
            setIsUploadOpen(true);
        }
    }
    useEffect(() => {
        if (!isUploadOpen) {
            if (profileImageURL === undefined) {
                setProfileImageURL(User);
            }
        }
    }, [isUploadOpen]);
    useEffect(()=>{
            if(profileImageURL !== User && profileImageURL !== undefined){
                setImageURL(baseUrl+profileImageURL)
            }
    },[profileImageURL])

    return (
        <div className={`StudentProfileFormContainer ${role === STUDENT_ROLE && 'hideOverflow'}`}>
            <div className="adminStudentFormContainer">
                <FormProvider {...methods}>
                    <form className="addStudentForm" onSubmit={handleSubmit(onSubmit)}>
                        <div className={`addStudentParentContainer ${Object.keys(errors).length ? 'hasErrorStyle' : ''}`}>
                            <div className="StudentProfileImage">
                                <div className={`${ImageURL ? 'StudentEditContainer' : 'StudentImageContainer'}`}>
                                    <img className={`${ImageURL ? 'StudentImageProfileStyle' : 'StudentImageDefault'}`} alt='TeacherImage' src={ImageURL} />
                                    <img onClick={handleClickUpload} className="uploadButtonMobile" alt="plus" src={uploadIconMobile} />
                                </div>
                                <div onClick={handleClickUpload} className={`uploadPhoto ${ !isEdit && "defaultCursor"}`}>
                                    <span>Upload photo</span>
                                    <img
                                        className="uploadButton"
                                        alt="plus"
                                        src={solidinterfaceplus}
                                    />
                                </div>
                                {
                                    isUploadOpen ?
                                        <FileUpload type='profile' setUrl={setProfileImageURL} open={isUploadOpen} setOpen={setIsUploadOpen} folderName="images" />
                                        : null
                                }
                            </div>
                            <div className="StudentDetails">
                                <div className="StudentNameField">
                                    <label className="nameOfStudent">Full Name</label>
                                    <InputField label="Full Name" name="fullName" classname="subjectStyle" type="text" size="addTeacherField" isDisable={!isEdit} />
                                </div>
                                <div className="StudentNameField">
                                    <label className="nameOfStudent">Username</label>
                                    <InputField label="Username" name="username" type="text" size="addTeacherField" isDisable={!isEdit} />
                                </div>
                                <div className="StudentNameField">
                                    <label className="nameOfStudent">Email</label>
                                    <InputField label="Email" name="email" type="text" size="addTeacherField" isDisable={!isEdit} onInput={handleInputWithoutSpaces}/>
                                </div>
                                <div className="StudentNameField">
                                    <label className="nameOfStudent">Phone Number</label>
                                    <InputField label="Phone Number" name="phonenumber" type="text" size="addTeacherField" isDisable={!isEdit} onInput={handleInputWithoutSpaces}/>
                                </div>
                                <div className="StudentNameField">
                                    <label className="nameOfStudent">Password</label>
                                    <InputField label="Password" name="password" type="password" size="addTeacherField" isDisable={!isEdit} />
                                </div>
                                {(role === STUDENT_ROLE) && (
                                    <div className="StudentNameField saveButtonMobile">
                                        <ButtonStyle paddingLeft='26px' paddingRight='28px' width='calc(100% - 60px)' height='35px' disableButton={updateStudentLoading || deleteStudentLoading} text={ isEdit ? "Save" : "Edit"} type="submit" onClick={() => isEdit ? handleSubmit(onSubmit)() : (setIsEdit && setIsEdit(true)) } />
                                    </div>
                                )}
                            </div>
                            {(role === SCHOOL_ADMIN) && (
                                <div className="StudentFormButton">
                                    <ButtonStyle paddingLeft={isMobileScreen ? '0px' : '16px'} paddingRight={isMobileScreen ? '0px' : '15px'} width={isMobileScreen ? 'auto' : '62px'} minWidth={isMobileScreen ? '20px' : '64px'} height='23.92px' color='var(--color-black)' bgcolor="white" variant='outlined' disableButton={updateStudentLoading || deleteStudentLoading || !isEdit} onClick={() => handleDelete()} deleteBtn={<img src={teacherDeleteIcon} alt="deleteIcon" className='deleteIconStyle' />} />
                                    <ButtonStyle paddingLeft='26px' paddingRight='28px' width={isMobileScreen ? '100%' : '83.25px'} height={isMobileScreen ? '40px' : '23.92px'} disableButton={updateStudentLoading || deleteStudentLoading || !isEdit} text="Save" type="submit" />
                                </div>
                            )}
                        </div>
                    </form>
                    <ConfirmationPopup
                        open={openConfirmationPopup}
                        message={`Are you sure you want to Delete this student?`}
                        gifData={DeleteGif}
                        yesButtonFunctionality={onYesButtonClick}
                        noButtonFunctionality={onNoButtonClick}
                        messageWidth={'195px'}
                    />
                </FormProvider>
            </div>
        </div>
    );
};

export default StudentProfileForm;

